// Copyright (c) 2026 杨翔宇（武汉文理学院）
// 物理智能教学智能体平台 (Physics Intelligent Agent)
// 保留所有权利 (All Rights Reserved)。未经著作权人书面许可，
// 不得以任何形式复制、传播、修改或用于商业用途。

import { ApiBase, type ApiRequestConfig } from './apiBase';
import type {
  IngestJobCreateRequest,
  IngestJobCreateResponse,
  IngestJobStatusResponse,
  KBHealthResponse,
  KBListResponse,
  KBStatsResponse,
  KnowledgeBaseConfig,
  KnowledgeBaseCreate,
  KnowledgeBasePatch,
  RetrievalMetricsResponse,
  RetrieveRequest,
  RetrieveResponse,
} from '@/types/kb';

export class ApiKb extends ApiBase {
  // Knowledge Base (KB)
  /**
   * KB 列表（rag-service 通过 api-gateway 统一入口：/api/v1/kb）
   */
  async kbList() {
    return this.client.get<KBListResponse>('/v1/kb', {
      suppressErrorToast: true,
      suppressUnauthorizedRedirect: true,
    } as ApiRequestConfig);
  }

  async kbGet(kbId: string) {
    return this.client.get<KnowledgeBaseConfig>(`/v1/kb/${kbId}`);
  }

  async kbCreate(payload: KnowledgeBaseCreate) {
    return this.client.post<KnowledgeBaseConfig>('/v1/kb', payload);
  }

  async kbReplace(kbId: string, payload: KnowledgeBaseCreate) {
    return this.client.put<KnowledgeBaseConfig>(`/v1/kb/${kbId}`, payload);
  }

  async kbPatch(kbId: string, payload: KnowledgeBasePatch) {
    return this.client.patch<KnowledgeBaseConfig>(`/v1/kb/${kbId}`, payload);
  }

  async kbDelete(kbId: string) {
    return this.client.delete<{ message?: string }>(`/v1/kb/${kbId}`);
  }

  async kbHealth(kbId: string) {
    return this.client.get<KBHealthResponse>(`/v1/kb/${kbId}/health`);
  }

  async kbStats(kbId: string) {
    return this.client.get<KBStatsResponse>(`/v1/kb/${kbId}/stats`);
  }

  async kbRetrievalMetrics(kbId: string, params?: { limit?: number }) {
    return this.client.get<RetrievalMetricsResponse>(`/v1/kb/${kbId}/metrics/retrieval`, {
      params,
    });
  }

  /**
   * 文档入库任务：
   * - 先调用 uploadFile 获取 file_id
   * - 再对指定 KB 创建 ingest job，并轮询 job 状态
   */
  async kbCreateIngestJob(kbId: string, payload: IngestJobCreateRequest) {
    return this.client.post<IngestJobCreateResponse>(`/v1/kb/${kbId}/ingest-jobs`, payload);
  }

  async kbGetIngestJob(kbId: string, jobId: string) {
    return this.client.get<IngestJobStatusResponse>(`/v1/kb/${kbId}/ingest-jobs/${jobId}`);
  }

  /**
   * 检索测试统一走 /v1/retrieve。
   */
  async retrieve(payload: RetrieveRequest) {
    return this.client.post<RetrieveResponse>('/v1/retrieve', payload);
  }
}
